import React, { useState } from 'react';
import { AnnouncementBar } from './components/AnnouncementBar';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { ProblemSection } from './components/ProblemSection';
import { WhatsIncluded } from './components/WhatsIncluded';
import { Testimonials } from './components/Testimonials';
import { KitsSection } from './components/KitsSection';
import { UpsellsSection } from './components/UpsellsSection';
import { CartSummary } from './components/CartSummary';
import { FAQ } from './components/FAQ';
import { FinalCTA } from './components/FinalCTA';
import { Footer } from './components/Footer';
import { PurchaseToast } from './components/PurchaseToast';
import { ExitIntentUpsellPopup } from './components/ExitIntentUpsellPopup';
import { ExtraComplementUpsellPopup } from './components/ExtraComplementUpsellPopup';
import { SecondKitUpsellPopup } from './components/SecondKitUpsellPopup';
import { BundleUpsellPopup } from './components/BundleUpsellPopup';
import { UPSELLS } from './data/upsells';
// TODO: replace with your real Shopify checkout URL
const SHOPIFY_CHECKOUT_URL = 'https://recetaspowerfit.myshopify.com/cart';
function openShopify() {
  window.open(SHOPIFY_CHECKOUT_URL, '_blank', 'noopener,noreferrer');
}
// Upsell funnel steps:
// 'none'      = no popup open
// 'secondKit' = add a 2nd main kit, both for $22.990 (when exactly 1 main kit, 0 complements)
// 'first'     = kit + 1 complement for $22.980 (other 0-complement cases)
// 'extra'     = add a 2nd complement, both for $19.990 (when 1 complement chosen)
// 'bundle'    = all 6 complements at 30% OFF (escalation when an offer is declined)
type UpsellStep = 'none' | 'secondKit' | 'first' | 'extra' | 'bundle';
export function App() {
  const [selectedKitIds, setSelectedKitIds] = useState<string[]>([]);
  const [selectedUpsellIds, setSelectedUpsellIds] = useState<string[]>([]);
  const [upsellStep, setUpsellStep] = useState<UpsellStep>('none');
  // Once the upsell funnel has been shown and resolved, checkout goes
  // straight to Shopify on subsequent clicks.
  const [upsellResolved, setUpsellResolved] = useState(false);
  const handleToggleKit = (id: string) => {
    setSelectedKitIds(prev => prev.includes(id) ? prev.filter(kitId => kitId !== id) : [...prev, id]);
  };
  const handleRemoveKit = (id: string) => {
    setSelectedKitIds(prev => prev.filter(kitId => kitId !== id));
  };
  const handleToggleUpsell = (id: string) => {
    setSelectedUpsellIds(prev => prev.includes(id) ? prev.filter(upsellId => upsellId !== id) : [...prev, id]);
  };
  const handleRemoveUpsell = (id: string) => {
    setSelectedUpsellIds(prev => prev.filter(upsellId => upsellId !== id));
  };
  /**
   * Checkout flow:
   * - No kit selected → nudge user to pick one
   * - Funnel NOT yet resolved → open the first upsell offer
   * - Funnel already resolved → go straight to Shopify
   */
  const handleCheckoutAttempt = () => {
    // Only block when the cart is completely empty. A customer who picked
    // just a complementary kit can continue without a main kit.
    const hasAnyItem = selectedKitIds.length > 0 || selectedUpsellIds.length > 0;
    if (!hasAnyItem) {
      alert('Por favor, elegí al menos un kit para continuar.');
      document.getElementById('kits')?.scrollIntoView({
        behavior: 'smooth'
      });
      return;
    }
    if (!upsellResolved) {
      // Branch the funnel by what's already in the cart.
      if (selectedKitIds.length === 0 && selectedUpsellIds.length === 1) {
        // ONLY one complementary kit (no main kit) → offer a 2nd complement,
        // both for $19.990.
        setUpsellStep('extra');
      } else if (selectedUpsellIds.length === 0) {
        if (selectedKitIds.length === 1) {
          // Only one main kit, no complement → offer a 2nd main kit.
          setUpsellStep('secondKit');
        } else {
          // Other 0-complement cases → kit + 1 complement offer.
          setUpsellStep('first');
        }
      } else if (selectedUpsellIds.length === 1) {
        // Mixed cart with exactly one complement → still offer a 2nd complement.
        setUpsellStep('extra');
      } else {
        // 2+ complements already → no extra offer, go straight to Shopify.
        setUpsellResolved(true);
        openShopify();
      }
      return;
    }
    openShopify();
  };
  // SECOND-KIT offer (2 products, both for $22.990): accept → add the chosen
  // second product to the right bucket (main kit or complement).
  const handleSecondKitAccept = (secondId: string, type: 'kit' | 'complement') => {
    if (type === 'kit') {
      setSelectedKitIds(prev => prev.includes(secondId) ? prev : [...prev, secondId]);
    } else {
      setSelectedUpsellIds(prev => prev.includes(secondId) ? prev : [...prev, secondId]);
    }
    setUpsellResolved(true);
    setUpsellStep('none');
    openShopify();
  };
  // FIRST offer (kit + 1 complement): accept → add complement and finish.
  const handleFirstAccept = (complementId: string) => {
    setSelectedUpsellIds(prev => prev.includes(complementId) ? prev : [...prev, complementId]);
    setUpsellResolved(true);
    setUpsellStep('none');
    openShopify();
  };
  // EXTRA offer (2nd complement, both for $19.990): accept → add extra.
  const handleExtraAccept = (extraComplementId: string) => {
    setSelectedUpsellIds(prev => prev.includes(extraComplementId) ? prev : [...prev, extraComplementId]);
    setUpsellResolved(true);
    setUpsellStep('none');
    openShopify();
  };
  // Declining either the first or the extra offer → escalate to the bundle.
  const handleEscalateToBundle = () => {
    setUpsellStep('bundle');
  };
  // BUNDLE offer (all 6 at 30% OFF): accept → add all and finish.
  const handleBundleAccept = () => {
    setSelectedUpsellIds(UPSELLS.map(u => u.id));
    setUpsellResolved(true);
    setUpsellStep('none');
    openShopify();
  };
  // BUNDLE decline → resolve the funnel and go to Shopify as-is.
  const handleBundleDecline = () => {
    setUpsellResolved(true);
    setUpsellStep('none');
    openShopify();
  };
  return <div className="min-h-screen bg-cream font-sans text-ink selection:bg-gold selection:text-white" data-id="element-0">
      <AnnouncementBar data-id="element-1" />
      <Navbar selectedKitIds={selectedKitIds} selectedUpsellIds={selectedUpsellIds} onRemoveUpsell={handleRemoveUpsell} onRemoveKit={handleRemoveKit} onCheckoutAttempt={handleCheckoutAttempt} data-id="element-2" />
      <main data-id="element-3">
        <Hero data-id="element-4" />
        <ProblemSection data-id="element-5" />
        <WhatsIncluded data-id="element-6" />
        <Testimonials data-id="element-7" />
        <KitsSection selectedKitIds={selectedKitIds} onToggleKit={handleToggleKit} data-id="element-8" />
        <UpsellsSection selectedUpsellIds={selectedUpsellIds} onToggleUpsell={handleToggleUpsell} data-id="element-9" />
        <div id="cart" className="scroll-mt-24" data-id="element-10" />
        <CartSummary selectedKitIds={selectedKitIds} selectedUpsellIds={selectedUpsellIds} onRemoveKit={handleRemoveKit} onRemoveUpsell={handleRemoveUpsell} onCheckoutAttempt={handleCheckoutAttempt} data-id="element-11" />
        <FAQ data-id="element-12" />
        <FinalCTA data-id="element-13" />
      </main>
      <Footer data-id="element-14" />
      <PurchaseToast data-id="element-15" />
      {/* Second-kit upsell: 2 main kits for $22.990 — when exactly 1 main kit, 0 complements */}
      <SecondKitUpsellPopup open={upsellStep === 'secondKit'} selectedKitIds={selectedKitIds} onAccept={handleSecondKitAccept} onDecline={handleEscalateToBundle} data-id="element-16" />
      {/* First upsell: kit + 1 complement ($22.980) — other 0-complement cases */}
      <ExitIntentUpsellPopup open={upsellStep === 'first'} selectedKitIds={selectedKitIds} onAccept={handleFirstAccept} onDecline={handleEscalateToBundle} data-id="element-17" />
      {/* Extra upsell: add a 2nd complement, both for $19.990 — when 1 complement */}
      <ExtraComplementUpsellPopup open={upsellStep === 'extra'} selectedUpsellIds={selectedUpsellIds} onAccept={handleExtraAccept} onDecline={handleEscalateToBundle} data-id="element-18" />
      {/* Final upsell: full bundle (escalation if an offer is declined) */}
      <BundleUpsellPopup open={upsellStep === 'bundle'} selectedKitIds={selectedKitIds} onAccept={handleBundleAccept} onDecline={handleBundleDecline} data-id="element-19" />
    </div>;
}